import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { Heading, Button } from "@bfc-software/pallet";
import { ListBox } from "react-aria-components";
import type { OrgSummary } from "@bfc-software/app-toolbox";
import { useEffect, useState, useCallback } from "react";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/aaa-api-client";
import { toast } from "@bfc-software/pallet";

import { LoginForm, GoBack } from "../-utilities/login-form-state";

import LocationListItem from "./location-list-item";
import UserName from "./user-name";
import LoginCard from "./login-card";
import styles from "./organization-select.module.css";

interface OrganizationSelectProps {
  lang: string;
  setParentForm: (newValues: Partial<LoginForm>) => void;
  orgs: OrgSummary[];
  email: string;
  flow: "login" | "reset-password";
  orgId: string;
  goBack: GoBack;
}

export default function OrganizationSelect({
  setParentForm,
  lang,
  orgs,
  goBack,
  email,
  orgId,
  flow,
}: OrganizationSelectProps) {
  const { t } = useTranslation({
    lang: lang,
    namespace: TRANSLATION_NAMESPACE.login,
  });

  const [emailSent, setEmailSent] = useState(false);
  const [emailTried, setEmailTried] = useState(false);

  const options = orgs.map((org) => ({
    id: org.org_id,
    name: org.org_name,
  }));

  const { mutate: setPasswordMutation, isPending: setPasswordPending } =
    apiClient().useMutation("post", "/auth/send-reset");

  const sendPasswordEmail = useCallback(async () => {
    setEmailTried(true);
    setPasswordMutation(
      {
        body: {
          email_addr: email,
          org_id: orgId,
        },
      },
      {
        onSuccess: () => {
          toast.success(t("emailEntry.emailSentSuccessfully"));
          setEmailSent(true);
        },
        onError: (error) => {
          if (error) {
            setParentForm({ email: "", flow: "login", orgId: "" });
            setEmailSent(false);
            toast.error(
              <div className={styles.errorToast}>
                <p className={styles.heading}>
                  {t("emailEntry.emailSentError")}
                </p>
                <p className={styles.errorMessage}>{error.message}</p>
              </div>,
            );
          }
        },
      },
    );
  }, [setPasswordMutation, email, orgId, setParentForm, t]);

  useEffect(() => {
    if (flow == "reset-password" && orgId && !emailTried) {
      sendPasswordEmail();
    }
  }, [flow, orgId, sendPasswordEmail, emailTried]);

  let organizationList;
  if (emailSent) {
    return (
      <LoginCard data-testid="organization-entry">
        <Heading text={t("emailEntry.title")} level="1" />
        <div className={styles.successMessage}>
          {t("emailEntry.checkResetPasswordEmail")}
        </div>
        <Button
          kind="text-icon-left"
          icon="ArrowLeft"
          onPress={() => {
            setParentForm({ email: "", flow: "login", orgId: "" });
            setEmailSent(false);
          }}
          variant="tertiary"
          text={t("emailEntry.signInADifferentWay")}
        />
      </LoginCard>
    );
  } else if (orgs.length) {
    //set orgs if exist
    organizationList = (
      // Extra wrapping div ensures corners don't get cut by scrollbars
      <div className={styles.locationPanelWrapper}>
        <ListBox
          onAction={(orgId) => {
            setParentForm({ orgId: orgId.toString() });
          }}
          className={styles.locationPanel}
          aria-label={t("organizationSelect.label")}
          selectionMode="single"
          items={options}
        >
          {(item) => (
            <LocationListItem
              textValue={item.name}
              isDisabled={setPasswordPending}
            />
          )}
        </ListBox>
      </div>
    );
  } else {
    //set orgs to empty
    organizationList = <div>{t("organizationSelect.noOrgs")}</div>;
  }
  return (
    <LoginCard data-testid="location-select">
      <Heading text={t("organizationSelect.title")} level="1" />
      <UserName>{email}</UserName>
      <div className={styles.selectionGroup}>
        {organizationList}
        {goBack.shouldRender && (
          <Button
            kind="text-icon-left"
            icon="ArrowLeft"
            onPress={goBack.handler}
            variant="tertiary"
            text={goBack.label}
            type="submit"
          />
        )}
      </div>
    </LoginCard>
  );
}
